import { Component, OnInit, Output, EventEmitter, OnDestroy } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AuthService, DailyReportService, SysNotifyService, TenantSettingsService, GaugeRefreshService, GmAssignmentService } from '@org/shared-services';
import { DailyReport, DailyReportResponse } from '@org/interfaces';
import { DailyReportTableComponent } from './daily-report-table.component';
import { ClosingGoalGaugeComponent } from './closing-goal-gauge.component';
import { MonthlyClosingGaugeComponent } from './monthly-closing-gauge.component';
import { SettingsModalTWComponent } from '../confirmation-dialog/settings-modal-dailyreport/settingsModalTW.component';
import * as moment from 'moment';

@Component({
    selector: 'lib-daily-reporting',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        DailyReportTableComponent,
        MonthlyClosingGaugeComponent,
        SettingsModalTWComponent
    ],
    providers: [DatePipe],
    templateUrl: './dailyReporting.component.html',
    styleUrl: './dailyReporting.component.scss'
})
export class DailyReportingComponent implements OnInit, OnDestroy {
  @Output() dataUpdated = new EventEmitter<void>();
  reportingForm: FormGroup;
  todayVariance: number = 0;
  monthlyVariance: number = 0;
  actualClosed: number = 0;
  todayGoal: number = 0;
  formattedDate: string = '';
  private tenantId!: string | null;
  currentTenant: string | null = null;
  cumulativeActual: number = 0;
  cumulativeGoal: number = 0;
  daysBehind: number = 0;
  totalWorkingDays: number = 0;
  daysLeft: number = 0;
  completedWorkingDays: number = 0;
  isEditMode: boolean = false;
  isOverrideMode: boolean = false;
  isPastDate: boolean = false;

  isSettingsModalOpen: boolean = false;
  monthlyBudget: number = 0;

  selectedGaugeDate: string = '';
  displayMonth: string = '';
  isCurrentMonth: boolean = true;

  private getTenantContext(): string {
    if (this.authService.currentUserValue && this.authService.currentUserValue.roles && this.authService.currentUserValue.roles.includes('gm')) {
      return this.currentTenant!;
    }
    return this.tenantId!;
  }

  constructor(
    private fb: FormBuilder, 
    private datePipe: DatePipe,
    private dailyReportService: DailyReportService,
    private authService: AuthService,
    private tenantSettingsService: TenantSettingsService,
    private sysNotify: SysNotifyService,
    private gaugeRefreshService: GaugeRefreshService,
    private gmAssignmentService: GmAssignmentService
  ) {
    const today = moment().utc();
    const localDate = today.format('YYYY-MM-DD');
    this.reportingForm = this.fb.group({
      reportDate: [localDate, Validators.required],
      actualClosed: [0, [Validators.required, Validators.min(0)]],
      tomorrowCloseGoal: [0, [Validators.required, Validators.min(0)]],
      dailyGP: [0, [Validators.required, Validators.min(0), Validators.max(100)]],
      todayGoal: [{value: 0, disabled: true}],
    });
    this.selectedGaugeDate = this.formatDateToString(today);
    this.updateDisplayMonth(today);
  }

  ngOnInit() {
    this.tenantId = this.authService.currentUserValue?.tenant;
    
    if (this.authService.currentUserValue && this.authService.currentUserValue.roles && this.authService.currentUserValue.roles.includes('gm')) {
      this.gmAssignmentService.getCurrentTenant().subscribe((res: any) => {
        this.currentTenant = res.data.tenant;
        this.initAfterTenant();
      });
    } else {
      this.initAfterTenant();
    }
  }

  private initAfterTenant() {
    this.loadTenantSettings();
    this.calculateVariances();
    
    // Initialize with today's date
    const today = moment().utc();
    const localDate = today.format('YYYY-MM-DD');
    
    // Set initial form values without triggering value changes
    this.reportingForm.patchValue({
      reportDate: localDate,
      actualClosed: 0,
      tomorrowCloseGoal: 0,
      dailyGP: 0,
      todayGoal: 0
    }, { emitEvent: false });
    
    this.formatDate(localDate);
    this.checkIfPastDate(localDate);
    
    // Load initial data for current date
    this.loadDailyReport(localDate);
    
    // Subscribe to date changes after initial setup
    this.reportingForm.get('reportDate')?.valueChanges.subscribe(date => {
      this.formatDate(date);
      this.checkIfPastDate(date);
      this.loadDailyReport(date);
    });
    
    this.selectedGaugeDate = localDate;
    this.updateDisplayMonth(today);
  }

  private formatDateToString(date: moment.Moment): string {
    // Ensure we keep the date in the local timezone for display
    return date.format('YYYY-MM-DD');
  }

  private updateDisplayMonth(date: moment.Moment) {
    // Keep the date in local timezone for display
    this.displayMonth = date.format('MMM YYYY');
  }

  private checkIfPastDate(date: string) {
    // Parse the date in local timezone first, then convert to UTC for comparison
    const selectedDate = moment(date).startOf('day').utc();
    const today = moment().startOf('day').utc();
    
    this.isPastDate = selectedDate.isBefore(today);
    
    if (!this.isPastDate) {
      this.isOverrideMode = false;
    }
    
    this.updateFormControlsState();
  }

  private updateFormControlsState() {
    const controls = ['actualClosed', 'tomorrowCloseGoal', 'dailyGP'];
    
    // If we're not in edit mode, always enable the form (new report)
    if (!this.isEditMode) {
      controls.forEach(controlName => {
        const control = this.reportingForm.get(controlName);
        if (control) {
          control.enable();
        }
      });
      return;
    }

    // For edit mode, check if we need override
    const canEdit = !this.isPastDate || this.isOverrideMode;
    controls.forEach(controlName => {
      const control = this.reportingForm.get(controlName);
      if (control) {
        if (canEdit) {
          control.enable();
        } else {
          control.disable();
        }
      }
    });
  }

  // Add method to check if submit button should be disabled
  isSubmitDisabled(): boolean {
    if (!this.isEditMode) {
      return false; // Always enable submit for new reports
    }
    
    // For edit mode, disable submit if it's a past date and override is off
    return this.isPastDate && !this.isOverrideMode;
  }

  toggleOverride() {
    this.isOverrideMode = !this.isOverrideMode;
    this.updateFormControlsState();
  }

  loadDailyReport(date: string) {
    if (date) {
      // Parse the date in local timezone first
      const selectedDate = moment(date);
      const currentDay = selectedDate.date();
      const year = selectedDate.year();
      const month = selectedDate.month() + 1;

      // Now get the daily report
      this.dailyReportService.getDailyReport(this.getTenantContext(), date).subscribe({
        next: (response) => {
          this.isEditMode = response.exists;

          // Set form values from the response
          this.reportingForm.patchValue({
            actualClosed: response.actualClosed || 0,
            tomorrowCloseGoal: response.tomorrowCloseGoal || 0,
            dailyGP: response.dailyGP || 0,
            todayGoal: response.todayGoal || 0
          }, { emitEvent: false });
          
          this.todayVariance = (response.actualClosed || 0) - (response.todayGoal || 0);
          this.monthlyVariance = response.cumulativeVariance;
          this.todayGoal = response.todayGoal || 0;

          // Reset form state
          this.reportingForm.markAsPristine();
          this.reportingForm.markAsUntouched();

          // Update form controls state based on whether we're editing or creating
          this.updateFormControlsState();
        },
        error: (error) => {
          console.error('Error loading daily report:', error);
          // Set default values when there's an error
          this.reportingForm.patchValue({
            actualClosed: 0,
            tomorrowCloseGoal: 0,
            dailyGP: 0,
            todayGoal: 0
          }, { emitEvent: false });
          
          this.todayVariance = 0;
          this.actualClosed = 0;
          this.todayGoal = 0;
          this.isEditMode = false;
          
          // Reset form state
          this.reportingForm.markAsPristine();
          this.reportingForm.markAsUntouched();
          
          // Enable form controls for new report submission
          this.updateFormControlsState();
          
          this.sysNotify.showError('Error loading daily report');
        }
      });
    }
  }
   

  loadMonthlyReport(year: number, month: number) {
    this.dailyReportService.getMonthlyReport(this.getTenantContext(), year, month).subscribe({
      next: (reports) => {
        if (!reports || !Array.isArray(reports) || reports.length === 0) {
          this.monthlyVariance = 0;
          return;
        }
        // Calculate monthly variance from array of daily reports
        this.monthlyVariance = reports.reduce((sum, report) => sum + report.dailyVariance, 0);
      },
      error: (error) => {
        console.error('Error loading monthly report:', error);
        // Handle error appropriately
      }
    });
  }

  formatDate(date: string) {
    if (date) {
      // Keep the date in local timezone for display
      this.formattedDate = moment(date).format('dddd, MMMM D, YYYY');
    }
  }

  calculateVariances() {
    const formValues = this.reportingForm.getRawValue();
    const actualClosed = Number(formValues.actualClosed) || 0;
    const todayGoal = Number(formValues.todayGoal) || 10500;
    this.todayVariance = actualClosed - todayGoal;
  }

  calculateTotalWorkingDaysOfMonth(date: string) {
    const selectedDate = moment.utc(date);
    const firstDay = selectedDate.clone().startOf('month');
    const lastDay = selectedDate.clone().endOf('month');
    
    let workingDays = 0;
    let currentDay = firstDay.clone();
    
    while (currentDay.isSameOrBefore(lastDay)) {
      if (currentDay.day() !== 0 && currentDay.day() !== 6) {
        workingDays++;
      }
      currentDay.add(1, 'day');
    }
    
    return workingDays;
  }

  calculateRemainingWorkingDaysOfMonth(date: string): number {
    const currentDate = moment.utc(date);
    const startOfMonth = currentDate.clone().startOf('month');
    
    let workingDays = 0;
    let currentDay = startOfMonth.clone();
    
    while (currentDay.isSameOrBefore(currentDate)) {
      if (currentDay.day() !== 0 && currentDay.day() !== 6) {
        workingDays++;
      }
      currentDay.add(1, 'day');
    }
    
    return workingDays;
  }

  onSubmit(): void {
    if (this.reportingForm.valid) {
      const formValues = this.reportingForm.getRawValue();
      
      // Don't submit if all values are 0 and it's not an edit
      if (!this.isEditMode && 
          formValues.actualClosed === 0 && 
          formValues.tomorrowCloseGoal === 0 && 
          formValues.dailyGP === 0) {
        this.sysNotify.showError('Please enter values before submitting');
        return;
      }

      const reportData = {
        date: formValues.reportDate,
        actualClosed: formValues.actualClosed,
        tomorrowCloseGoal: formValues.tomorrowCloseGoal,
        dailyGP: formValues.dailyGP,
        tenantId: this.getTenantContext()
      };

      const submitObservable = this.isEditMode
        ? this.dailyReportService.updateDailyReport(this.getTenantContext(), formValues.reportDate, reportData)
        : this.dailyReportService.submitDailyReport(reportData);

      submitObservable.subscribe({
        next: (response) => {
          const action = this.isEditMode ? 'updated' : 'submitted';
          this.sysNotify.showSuccess(`Report ${action} successfully`, 5000, "right", "top");
          
          // Refresh monthly report after submission
          const date = moment(formValues.reportDate);
          this.loadMonthlyReport(date.year(), date.month() + 1);
          
          // Reload the daily report to get fresh data
          this.loadDailyReport(formValues.reportDate);

          // Reset form dirty state
          this.reportingForm.markAsPristine();
          this.reportingForm.markAsUntouched();

          // Update the selected gauge date to trigger a refresh
          this.selectedGaugeDate = formValues.reportDate;
          this.updateDisplayMonth(date);

          // Trigger gauge refresh through the service
          this.gaugeRefreshService.triggerRefresh();

          // Emit event to notify that data has been updated
          this.dataUpdated.emit();
        },
        error: (error) => {
          console.error('Error submitting report:', error);
          this.sysNotify.showError(`Error ${this.isEditMode ? 'updating' : 'submitting'} report`);
        }
      });
    }
  }

  private loadTenantSettings(): void {
    const tenantContext = this.getTenantContext();
    if (tenantContext) {
      this.tenantSettingsService.getTenantSettings().subscribe({
        next: (settings) => {
          this.monthlyBudget = settings.monthlyBudget;
        },
        error: (error) => {
          console.error('Error loading tenant settings:', error);
        }
      });
    }
  }

  openSettingsModal(): void {
    this.isSettingsModalOpen = true;
  }

  onSettingsSave(settings: { monthlyBudget: number }): void {
    this.monthlyBudget = settings.monthlyBudget;
    this.isSettingsModalOpen = false;
    // Here you would save the settings to your backend
    const tenantContext = this.getTenantContext();
    if (tenantContext) {
      this.tenantSettingsService.updateTenantSettings({
        monthlyBudget: settings.monthlyBudget
      }).subscribe({
        next: (updatedSettings) => {
          this.monthlyBudget = updatedSettings.monthlyBudget;
          this.isSettingsModalOpen = false;
          this.sysNotify.showSuccess('Settings saved successfully', 5000, "right", "top");
          // Optionally refresh any calculations that depend on monthlyBudget
          this.loadTenantSettings();
          // Trigger gauge refresh after saving settings
          this.gaugeRefreshService.triggerRefresh();
        },
        error: (error) => {
          console.error('Error saving tenant settings:', error);
          this.sysNotify.showError('Error saving tenant settings');
          // Handle error (show error message, etc.)
        }
      });
    }

  }

  onSettingsCancel(): void {
    this.isSettingsModalOpen = false;
  }

  previousMonth() {
    const currentDate = moment(this.selectedGaugeDate);
    currentDate.subtract(1, 'month');
    this.selectedGaugeDate = this.formatDateToString(currentDate);
    this.updateDisplayMonth(currentDate);
    this.checkIfCurrentMonth(currentDate);
  }

  nextMonth() {
    const currentDate = moment(this.selectedGaugeDate);
    const today = moment();
    
    if (currentDate.month() < today.month() || 
        currentDate.year() < today.year()) {
      currentDate.add(1, 'month');
      this.selectedGaugeDate = this.formatDateToString(currentDate);
      this.updateDisplayMonth(currentDate);
      this.checkIfCurrentMonth(currentDate);
    }
  }

  private checkIfCurrentMonth(date: moment.Moment) {
    const today = moment().utc();
    this.isCurrentMonth = date.month() === today.month() && 
                         date.year() === today.year();
  }

  refreshMonthlyData() {
    const date = moment(this.selectedGaugeDate);
    this.loadMonthlyReport(date.year(), date.month() + 1);
  }

  ngOnDestroy() {
    // Cleanup code if needed
  }
}
